// Manage Teams Modal Component
// For assigning users to teams (individual or bulk)

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Search, ChevronDown, Users as UsersIcon, Shield, Code, Settings, Package, ShieldCheck, Wrench } from 'lucide-react';
import type { User, Team } from '@/types/teams';
import { useToast } from '@/hooks/use-toast';

const TEAM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  users: UsersIcon,
  shield: Shield,
  code: Code,
  settings: Settings,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
};

interface ManageTeamsModalProps {
  readonly users: User[];
  readonly teams: Team[];
  readonly onClose: () => void;
  readonly onSave: () => void;
}

export function ManageTeamsModal({
  users,
  teams,
  onClose,
  onSave,
}: Readonly<ManageTeamsModalProps>) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(() => {
    // For single user, pre-select their current teams
    if (users.length === 1) {
      return users[0].teams.map(team => typeof team === 'string' ? team : team.teamId);
    }
    return [];
  });
  const [assignmentMode, setAssignmentMode] = useState<'add' | 'replace'>('add');
  const [showUserList, setShowUserList] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isBulk = users.length > 1;

  // Filter teams by search
  const filteredTeams = useMemo(() => {
    if (!searchQuery) return teams;
    const query = searchQuery.toLowerCase();
    return teams.filter(t =>
      t.name.toLowerCase().includes(query) ||
      t.description.toLowerCase().includes(query)
    );
  }, [teams, searchQuery]);

  // Separate default and custom teams
  const defaultTeams = filteredTeams.filter(t => t.isDefault);
  const customTeams = filteredTeams.filter(t => !t.isDefault);

  // Get checkbox state for bulk operations
  const getTeamCheckboxState = (teamId: string): boolean | 'indeterminate' => {
    if (!isBulk) {
      return selectedTeamIds.includes(teamId);
    }

    const usersInTeam = users.filter(u => u.teams.includes(teamId)).length;
    if (usersInTeam === 0) return false;
    if (usersInTeam === users.length) return true;
    return 'indeterminate';
  };

  const handleToggleTeam = (teamId: string, checked: boolean) => {
    // Standard Users team cannot be unchecked if it's the only team
    const standardUsersTeam = teams.find(t => t.id === 'team_default_standard');
    
    if (!checked && teamId === standardUsersTeam?.id) {
      if (selectedTeamIds.length === 1) {
        toast({
          title: 'Cannot remove last team',
          description: 'Users must belong to at least one team',
          variant: 'destructive',
        });
        return;
      }
    }

    if (checked) {
      setSelectedTeamIds([...selectedTeamIds, teamId]);
    } else {
      setSelectedTeamIds(selectedTeamIds.filter(id => id !== teamId));
    }
  };

  const handleSave = async () => {
    // Validate at least one team selected
    if (selectedTeamIds.length === 0) {
      toast({
        title: 'No teams selected',
        description: 'Please select at least one team',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      toast({
        title: 'Success',
        description: isBulk
          ? `${users.length} users assigned to teams successfully`
          : `${users[0].name} team assignments updated`,
      });
      
      onSave();
    } catch (error) {
      console.error('Failed to save team assignments:', error);
      toast({
        title: 'Error',
        description: 'Failed to save team assignments. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (users.length === 1) {
      const currentTeams = users[0].teams
        .map(team => typeof team === 'string' ? team : team.teamId)
        .sort((a, b) => a.localeCompare(b));
      const newTeams = [...selectedTeamIds].sort((a, b) => a.localeCompare(b));
      return JSON.stringify(currentTeams) !== JSON.stringify(newTeams);
    }
    return selectedTeamIds.length > 0;
  }, [users, selectedTeamIds]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isBulk
              ? `Manage Teams for ${users.length} Users`
              : `Manage Teams for ${users[0].name}`}
          </DialogTitle>
          <DialogDescription>
            {isBulk
              ? 'Assign multiple users to teams at once'
              : 'Select which teams this user should belong to'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* User Summary for Bulk */}
          {isBulk && (
            <Collapsible open={showUserList} onOpenChange={setShowUserList}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-between">
                  <span>
                    You are managing team assignments for {users.length} users
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showUserList ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <ScrollArea className="h-32 border rounded-md p-3">
                  <div className="space-y-1">
                    {users.map(user => (
                      <div key={user.id} className="text-sm">
                        {user.name} ({user.email})
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Assignment Mode for Bulk */}
          {isBulk && (
            <div className="space-y-2">
              <Label>Assignment Mode</Label>
              <RadioGroup value={assignmentMode} onValueChange={(v) => setAssignmentMode(v as 'add' | 'replace')}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="add" id="add" />
                  <Label htmlFor="add" className="font-normal cursor-pointer">
                    Add to selected teams (keep existing teams)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="replace" id="replace" />
                  <Label htmlFor="replace" className="font-normal cursor-pointer">
                    Replace with selected teams (remove from current teams)
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search teams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Teams List */}
          <ScrollArea className="flex-1 border rounded-md">
            <div className="p-3 space-y-1">
              {/* Default Teams */}
              {defaultTeams.length > 0 && (
                <>
                  {defaultTeams.map(team => {
                    const IconComponent = TEAM_ICONS[team.icon] || UsersIcon;
                    const checkState = getTeamCheckboxState(team.id);
                    const isStandardUsers = team.id === 'team_default_standard';
                    const isOnlyTeam = selectedTeamIds.length === 1 && selectedTeamIds[0] === team.id;

                    return (
                      <div
                        key={team.id}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          checked={checkState === true || checkState === 'indeterminate'}
                          onCheckedChange={(checked) => handleToggleTeam(team.id, checked as boolean)}
                          disabled={isStandardUsers && isOnlyTeam}
                        />
                        <div
                          className="h-1 w-1 rounded-full flex-shrink-0"
                          style={{ backgroundColor: team.color, width: '4px', height: '100%' }}
                        />
                        <div
                          className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${team.color}20`, color: team.color }}
                        >
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{team.name}</span>
                            <Badge variant="outline" className="text-xs">
                              Default Team
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {team.memberCount} members
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-t my-2" />
                </>
              )}

              {/* Custom Teams */}
              {customTeams.map(team => {
                const IconComponent = TEAM_ICONS[team.icon] || UsersIcon;
                const checkState = getTeamCheckboxState(team.id);

                return (
                  <div
                    key={team.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      checked={checkState === true || checkState === 'indeterminate'}
                      onCheckedChange={(checked) => handleToggleTeam(team.id, checked as boolean)}
                    />
                    <div
                      className="h-1 w-1 rounded-full flex-shrink-0"
                      style={{ backgroundColor: team.color, width: '4px', height: '100%' }}
                    />
                    <div
                      className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${team.color}20`, color: team.color }}
                    >
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm">{team.name}</span>
                      <p className="text-xs text-muted-foreground">
                        {team.memberCount} members
                      </p>
                    </div>
                  </div>
                );
              })}

              {filteredTeams.length === 0 && (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No teams found matching "{searchQuery}"
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
